import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import DonutSplitLayout from "@/components/ui/DonutSplitLayout";
import { formatNumber } from "@/lib/pilotageUi";

const COLORS = [
  "#3B82F6",
  "#8B5CF6",
  "#06B6D4",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
];
const MAX_DEPARTMENTS = 10;
const MAX_DONUT_CATEGORIES = 5;
const normalizeDepartmentData = (data = []) => {
  const normalizedData = data
    .map((item) => ({
      department: item?.department || item?.name || "Non renseigné",
      count: Number(item?.count ?? item?.value ?? 0),
    }))
    .filter(({ count }) => Number.isFinite(count) && count >= 0)
    .sort(
      (left, right) =>
        right.count - left.count ||
        left.department.localeCompare(right.department, "fr-FR"),
    );

  if (normalizedData.length <= MAX_DEPARTMENTS) {
    return normalizedData;
  }

  const topDepartments = normalizedData.slice(0, MAX_DEPARTMENTS);
  const otherDepartmentsCount = normalizedData
    .slice(MAX_DEPARTMENTS)
    .reduce((sum, item) => sum + item.count, 0);

  if (!otherDepartmentsCount) {
    return topDepartments;
  }

  return [
    ...topDepartments,
    {
      department: "Autres",
      count: otherDepartmentsCount,
    },
  ];
};

const DepartmentTooltip = ({ active, payload, total }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const { department, count } = payload[0].payload;
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="bg-slate-900 border border-white/10 rounded-lg p-3 shadow-xl">
      <p className="text-white font-medium">{department}</p>
      <p className="text-lenvia-blue">
        {formatNumber(count)} candidature{count > 1 ? "s" : ""}
      </p>
      <p className="text-xs text-slate-400">{percentage.toFixed(0)}% du total</p>
    </div>
  );
};

const DepartmentDonut = ({
  data = [],
  title = "Répartition par département",
}) => {
  const chartData = normalizeDepartmentData(data);
  const total = chartData.reduce((sum, item) => sum + item.count, 0);
  const leadingDepartment = chartData[0];
  const leadingPercentage = total > 0 && leadingDepartment
    ? Math.round((leadingDepartment.count / total) * 100)
    : 0;
  const useRankedBars = chartData.length > MAX_DONUT_CATEGORIES;

  return (
    <div className="w-full min-w-0 space-y-4">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      {chartData.length > 0 ? (
        <>
          <p className="text-sm leading-relaxed text-slate-300" data-testid="department-chart-summary">
            {leadingDepartment.department} représente la part la plus importante avec {leadingPercentage}% du total
            ({formatNumber(leadingDepartment.count)} candidature{leadingDepartment.count > 1 ? "s" : ""}).
          </p>

          {useRankedBars ? (
            <ul className="space-y-3" aria-label={`Classement pour ${title.toLowerCase()}`}>
              {chartData.map((department, index) => {
                const percentage = total > 0 ? Math.round((department.count / total) * 100) : 0;
                return (
                  <li key={`${department.department}-${department.count}-bar`} className="space-y-1.5">
                    <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm">
                      <span className="min-w-0 break-words text-slate-200">{department.department}</span>
                      <span className="shrink-0 font-medium tabular-nums text-white">
                        {formatNumber(department.count)} · {percentage}%
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]" aria-hidden="true">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <DonutSplitLayout
              className="w-full min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,280px)]"
              chartClassName="mx-auto w-[240px] h-[240px] sm:w-[280px] sm:h-[280px] min-w-0"
              legendClassName="w-full min-w-0 space-y-2 overflow-y-auto max-h-[280px] xl:max-w-[280px] pr-1"
              chart={(
                <div aria-hidden="true" className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="count" nameKey="department" label={({ percent }) => Number.isFinite(percent) ? `${(percent * 100).toFixed(0)}%` : ""} labelLine={false}>
                        {chartData.map((entry, index) => <Cell key={`${entry.department}-${entry.count}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={(tooltipProps) => <DepartmentTooltip {...tooltipProps} total={total} />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-white">{formatNumber(total)}</span>
                    <span className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">Total</span>
                  </div>
                </div>
              )}
              legend={chartData.map((department, index) => (
                <div key={`${department.department}-${department.count}-legend`} className="flex w-full min-w-0 max-w-sm items-center gap-3">
                  <div className="h-3 w-3 flex-shrink-0 rounded-full" aria-hidden="true" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                  <span className="min-w-0 flex-1 truncate text-sm leading-relaxed text-slate-300" title={department.department}>{department.department}</span>
                  <span className="flex-none text-right text-sm font-medium text-white">{formatNumber(department.count)}</span>
                </div>
              ))}
            />
          )}

          <details className="rounded-xl border border-white/10 bg-white/[0.02]">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lenvia-blue">
              Consulter les données détaillées
            </summary>
            <div className="overflow-x-auto border-t border-white/10">
              <table className="w-full min-w-[420px] text-left text-sm">
                <caption className="sr-only">Données de {title.toLowerCase()}</caption>
                <thead className="text-slate-300"><tr><th className="px-4 py-3" scope="col">Département</th><th className="px-4 py-3 text-right" scope="col">Candidatures</th><th className="px-4 py-3 text-right" scope="col">Part</th></tr></thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {chartData.map((department) => <tr key={`${department.department}-${department.count}-row`}><th className="px-4 py-3 font-normal text-slate-200" scope="row">{department.department}</th><td className="px-4 py-3 text-right tabular-nums text-white">{formatNumber(department.count)}</td><td className="px-4 py-3 text-right tabular-nums text-slate-300">{total > 0 ? Math.round((department.count / total) * 100) : 0}%</td></tr>)}
                </tbody>
              </table>
            </div>
          </details>
        </>
      ) : (
        <div className="h-[250px] flex items-center justify-center text-slate-500">
          Pas de données disponibles
        </div>
      )}
    </div>
  );
};

export default DepartmentDonut;
